import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      // Always show success (security)
      setIsSubmitted(true);
      
    } catch (error) {
      console.error('Password reset error:', error);
      // Still show success for security
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center py-12 px-4"
        style={{ backgroundColor: '#0B1220', color: '#E8ECF2' }}
      >
        <Toaster position="top-center" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card 
            className="p-8 text-center"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
          >
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              E-Mail gesendet!
            </h2>
            <p className="text-slate-400 mb-6">
              Falls ein Account mit <strong className="text-white">{email}</strong> existiert, 
              erhalten Sie in Kürze eine E-Mail mit weiteren Anweisungen.
            </p>
            <Link to="/login">
              <Button className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white">
                Zurück zum Login
              </Button>
            </Link>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4"
      style={{ backgroundColor: '#0B1220', color: '#E8ECF2' }}
    >
      <Toaster position="top-center" />
      
      <div className="w-full max-w-md">
        <Link 
          to="/login"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zum Login
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-[#2F80FF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-[#2F80FF]" />
          </div>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Passwort vergessen?
          </h1>
          <p className="text-slate-400">
            Kein Problem! Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card 
            className="p-6"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-white">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="forgot-email"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-[#0E1628] border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              <Button
                type="submit"
                data-testid="forgot-submit"
                className="w-full bg-[#2F80FF] hover:bg-[#4A92FF] text-white h-11"
                disabled={isLoading}
              >
                {isLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gesendet...</>
                ) : (
                  'Link senden'
                )}
              </Button>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
